import { useEffect } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  type ErrorComponentProps,
  HeadContent,
  Link,
  Outlet,
  Scripts,
  useRouter,
} from "@tanstack/react-router";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AnalyticsTracker } from "@/lib/analytics";
import { reportLovableError } from "@/lib/lovable-error-reporting";
import NotFound from "@/pages/NotFound";
import { serializeJsonLd } from "@/components/SEOHead";
import appCss from "../styles.css?url";
import interCss from "@fontsource-variable/inter/index.css?url";
import heroImage from "@/assets/hero-developer-dark.webp";

// Ported from index.html: Google tag with Consent Mode v2. Page views are sent by
// src/lib/analytics.ts on every route change, so send_page_view stays false.
const GTAG_BOOTSTRAP = `
window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('consent', 'default', { ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied', analytics_storage: 'granted' });
gtag('consent', 'default', { analytics_storage: 'denied', region: ['AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','HU','IE','IT','LV','LT','LU','MT','NL','PL','PT','RO','SK','SI','ES','SE','IS','LI','NO','GB','CH'] });
gtag('js', new Date());
gtag('config', 'G-VWP4HG0GWV', { send_page_view: false });
function loadGtm(){
  var s = document.createElement('script');
  s.async = true;
  s.src = 'https://www.googletagmanager.com/gtag/js?id=G-VWP4HG0GWV';
  document.head.appendChild(s);
}
if (document.readyState === 'complete') {
  if ('requestIdleCallback' in window) { requestIdleCallback(loadGtm, { timeout: 3000 }); } else { setTimeout(loadGtm, 2000); }
} else {
  window.addEventListener('load', function(){
    if ('requestIdleCallback' in window) { requestIdleCallback(loadGtm, { timeout: 3000 }); } else { setTimeout(loadGtm, 2000); }
  });
}
`;

// Ported from index.html: Microsoft Clarity.
const CLARITY_BOOTSTRAP = `
(function(c,l,a,r,i,t,y){
  c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
  t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
  y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
})(window, document, "clarity", "script", "xhck6mbp5k");
`;

const ORGANIZATION_LD = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "NETIUS LLC",
  alternateName: "NETIUS",
  url: "https://netius.com",
  logo: "https://netius.com/favicon.png",
  description:
    "NETIUS is a national and global web development agency building high-performance, custom websites for ambitious founders. Headquartered in Orlando, Florida, serving clients across the US and worldwide—delivered in 3-7 days.",
  foundingDate: "2010",
  founder: { "@type": "Person", name: "Dan", jobTitle: "Founder & Lead Developer" },
  address: { "@type": "PostalAddress", addressLocality: "Orlando", addressRegion: "FL", addressCountry: "US" },
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+1-323-638-4871",
    contactType: "customer service",
    email: "web@netius.com",
    availableLanguage: "English",
    areaServed: ["US", "Worldwide", "Orlando", "Central Florida"],
  },
  sameAs: [
    "https://neti.us/git",
    "https://neti.us/yt",
    "https://neti.us/x",
    "https://neti.us/fb",
    "https://neti.us/ig",
    "https://neti.us/ld",
  ],
};

const WEBSITE_LD = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "NETIUS",
  url: "https://netius.com",
  description: "National and global high-performance web development. Headquartered in Orlando, FL.",
  publisher: { "@type": "Organization", name: "NETIUS LLC" },
};

const city = (name: string) => ({ "@type": "City", name });

const PROFESSIONAL_SERVICE_LD = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  name: "NETIUS LLC",
  image: "https://netius.com/favicon.png",
  url: "https://netius.com",
  telephone: "+1-323-638-4871",
  email: "web@netius.com",
  description:
    "National and global web development agency headquartered in Orlando, FL. Custom, high-performance websites delivered in 3-7 days for clients across Central Florida, the US, and worldwide.",
  priceRange: "$$",
  address: { "@type": "PostalAddress", addressLocality: "Orlando", addressRegion: "FL", addressCountry: "US" },
  geo: { "@type": "GeoCoordinates", latitude: 28.5384, longitude: -81.3789 },
  openingHours: "Mo-Fr 09:00-17:00",
  paymentAccepted: "Credit Card, Bank Transfer",
  areaServed: [
    ...[
      "Orlando",
      "Winter Park",
      "Kissimmee",
      "Lake Mary",
      "Sanford",
      "Altamonte Springs",
      "Maitland",
      "Winter Garden",
      "Apopka",
      "Oviedo",
      "Lake Nona",
      "Celebration",
      "Clermont",
    ].map(city),
    { "@type": "State", name: "Florida" },
    { "@type": "Country", name: "United States" },
    { "@type": "Place", name: "Worldwide" },
  ],
  serviceArea: [
    {
      "@type": "GeoCircle",
      geoMidpoint: { "@type": "GeoCoordinates", latitude: 28.5384, longitude: -81.3789 },
      geoRadius: "75 mi",
    },
    { "@type": "Country", name: "United States" },
    { "@type": "Place", name: "Worldwide" },
  ],
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Web Development Services",
    itemListElement: [
      ["Custom Website Design & Development", "High-performance custom websites delivered in 3-7 days"],
      ["WordPress & Wix Migration", "Migrate from legacy platforms to modern, high-performance architecture"],
      ["Website Maintenance", "Ongoing website support and maintenance plans starting at $197/month"],
      ["Performance Optimization", "Speed optimization and Core Web Vitals improvement for existing websites"],
    ].map(([name, description]) => ({ "@type": "Offer", itemOffered: { "@type": "Service", name, description } })),
  },
};

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  // Page-specific title, description, canonical, robots and social tags come from each
  // page's SEOHead (React hoists them into <head>), so none are set here.
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1.0" },
      { name: "author", content: "NETIUS LLC" },
      { name: "theme-color", content: "#0a0a0a" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" },
      { name: "apple-mobile-web-app-title", content: "NETIUS" },
      { name: "format-detection", content: "telephone=no" },
      { name: "msapplication-TileColor", content: "#0a0a0a" },
      { name: "geo.region", content: "US-FL" },
      { name: "geo.placename", content: "Orlando, Florida" },
      { name: "geo.position", content: "28.5384;-81.3789" },
      { name: "ICBM", content: "28.5384, -81.3789" },
      { name: "google-site-verification", content: "cDDOX-mDzdO3-wYYWyrxjEMfu2Pkylr8ljLecw_GJLM" },
    ],
    links: [
      { rel: "stylesheet", href: interCss },
      { rel: "stylesheet", href: appCss },
      { rel: "preload", href: heroImage, as: "image", type: "image/webp", fetchPriority: "high" },
      { rel: "icon", type: "image/png", href: "/favicon.png" },
      { rel: "apple-touch-icon", href: "/favicon.png" },
    ],
    scripts: [
      { children: GTAG_BOOTSTRAP },
      { children: CLARITY_BOOTSTRAP },
      { type: "application/ld+json", children: serializeJsonLd(ORGANIZATION_LD) },
      { type: "application/ld+json", children: serializeJsonLd(WEBSITE_LD) },
      { type: "application/ld+json", children: serializeJsonLd(PROFESSIONAL_SERVICE_LD) },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFound,
  errorComponent: RootErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" className="dark" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <AnalyticsTracker />
        <Outlet />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

function RootErrorComponent({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  useEffect(() => {
    console.error(error);
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);
  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-foreground px-4">
      <div className="max-w-md text-center">
        <title>This page didn't load | NETIUS</title>
        <meta name="robots" content="noindex, nofollow" />
        <h1 className="text-2xl font-bold mb-3">This page didn't load</h1>
        <p className="text-muted-foreground mb-6">Something went wrong on our end. You can try again or head back home.</p>
        <div className="flex gap-3 justify-center">
          <button
            type="button"
            className="px-4 py-2 rounded-md bg-primary text-primary-foreground"
            onClick={() => {
              router.invalidate();
              reset();
            }}
          >
            Try again
          </button>
          <Link to="/" className="px-4 py-2 rounded-md border border-border">
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}
